<script setup>
/* global axios */
// Katalogu besleyen dis API baglantilari.
//
// YON TEK: disaridan okuyoruz, musterinin magaza verisine hicbir sey
// yazmiyoruz. Yanlis eslenen tek bir alan gercek fiyati bozar.
//
// Kurulum sihirbazla: musteri dokuman adresini, kendi API adresini ve
// kimligini veriyor; hangi ucun urunleri listeledigini, yanitin neresinde
// dizi oldugunu ve hangi alanin fiyat oldugunu model cikariyor. Emin
// olamadigi yerde SORU soruyor - uydurulmus bir esleme sessizce yanlis
// fiyat gosterir.
//
// Kimlik bilgileri modele GITMEZ: ayri alanlardan alinip dogrudan
// baglantiya yaziliyor.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const route = useRoute();

const YOKLAMA_MS = 2500;
const taban = computed(() => `/api/v1/accounts/${route.params.accountId}/katalog-api`);

const baglantilar = ref([]);
const yukleniyor = ref(true);
const hata = ref('');

// --- sihirbaz durumu ---
const formAcik = ref(false);
const kaynak = ref('');
const temelAdres = ref('');
const kullanici = ref('');
const parola = ref('');
const dosya = ref(null);
const calisiyor = ref(false);
const asama = ref('');
const oturum = ref('');
const uclar = ref([]);
const seciliUc = ref(null);
const ayar = ref(null);
const cevaplar = ref({});

let zamanlayici = null;
const durdur = () => {
  if (zamanlayici) clearTimeout(zamanlayici);
  zamanlayici = null;
};
onUnmounted(durdur);

const yukle = async (sessiz = false) => {
  if (!sessiz) yukleniyor.value = true;
  try {
    const { data } = await axios.get(taban.value);
    baglantilar.value = data.baglantilar || [];
  } catch (e) {
    hata.value = e?.response?.data?.error || t('KATALOG_API.HATA');
  } finally {
    yukleniyor.value = false;
  }
};

onMounted(yukle);

// Senkron arka planda calisir (Sidekiq). Kayit, "simdi" ya da "baslat"
// sonrasi kullanici sonucu sayfayi yenilemeden gorsun: baglantinin son
// senkron / hata damgasi degisene kadar liste sessizce tazelenir.
const IZLEME_MS = 3000;
const AZAMI_IZLEME = 60; // ~3 dakika
const bekleyen = ref(null);
let izleyici = null;
const izlemeyiBirak = () => {
  if (izleyici) clearTimeout(izleyici);
  izleyici = null;
  bekleyen.value = null;
};
onUnmounted(izlemeyiBirak);

const damga = id => {
  const b = baglantilar.value.find(x => x.id === id);
  return b ? `${b.son_senkron_at}|${b.son_hata}` : null;
};

const senkronuIzle = id => {
  izlemeyiBirak();
  const once = damga(id);
  if (once === null) return;
  bekleyen.value = id;
  let kalan = AZAMI_IZLEME;
  const bak = async () => {
    await yukle(true);
    kalan -= 1;
    const simdiki = damga(id);
    if (simdiki === null || simdiki !== once || kalan <= 0) {
      izlemeyiBirak();
      return;
    }
    izleyici = setTimeout(bak, IZLEME_MS);
  };
  izleyici = setTimeout(bak, IZLEME_MS);
};

const isiBekle = isId =>
  new Promise((coz, reddet) => {
    const bak = async () => {
      try {
        const { data } = await axios.get(`${taban.value}/is/${isId}`);
        if (data.durum === 'calisiyor') {
          zamanlayici = setTimeout(bak, YOKLAMA_MS);
          return;
        }
        if (data.durum === 'hazir') coz(data.sonuc);
        else reddet(new Error(data.hata || t('KATALOG_API.HATA')));
      } catch (e) {
        reddet(new Error(e?.response?.data?.error || t('KATALOG_API.HATA')));
      }
    };
    zamanlayici = setTimeout(bak, YOKLAMA_MS);
  });

const cozumle = async () => {
  calisiyor.value = true;
  asama.value = 'cozumle';
  hata.value = '';
  try {
    const { data } = await axios.post(`${taban.value}/cozumle`, {
      oturum: oturum.value,
      uc_index: seciliUc.value,
      cevaplar: cevaplar.value,
    });
    const cikti = await isiBekle(data.is_id);
    ayar.value = cikti.ayar;
  } catch (e) {
    hata.value = e.message || t('KATALOG_API.HATA');
  } finally {
    calisiyor.value = false;
    asama.value = '';
  }
};

const incele = async () => {
  if (!kaynak.value.trim() && !dosya.value) return;

  durdur();
  calisiyor.value = true;
  asama.value = 'incele';
  hata.value = '';
  ayar.value = null;
  cevaplar.value = {};

  try {
    let istek;
    if (dosya.value) {
      const govde = new FormData();
      govde.append('dosya', dosya.value);
      govde.append('kaynak', kaynak.value);
      govde.append('temel_adres', temelAdres.value);
      istek = axios.post(`${taban.value}/kesfet`, govde);
    } else {
      istek = axios.post(`${taban.value}/kesfet`, {
        kaynak: kaynak.value,
        temel_adres: temelAdres.value,
      });
    }

    const { data } = await istek;
    const cikti = await isiBekle(data.is_id);
    oturum.value = cikti.oturum;
    uclar.value = cikti.uclar || [];
    seciliUc.value = uclar.value.length ? uclar.value[0].index : null;
    calisiyor.value = false;
    await cozumle();
  } catch (e) {
    calisiyor.value = false;
    asama.value = '';
    hata.value = e.message || t('KATALOG_API.HATA');
  }
};

const kaydet = async () => {
  if (!ayar.value) return;

  calisiyor.value = true;
  hata.value = '';
  try {
    const a = ayar.value;
    const { data } = await axios.post(taban.value, {
      baglanti: {
        ad: a.ad,
        temel_adres: temelAdres.value,
        kimlik_turu: a.kimlik_turu,
        kimlik_ayari: { username: kullanici.value, password: parola.value },
        liste_ayari: {
          yol: a.yol,
          yontem: a.yontem,
          govde_sablonu: a.govde_sablonu,
          dizi_yolu: a.dizi_yolu,
          sayfalama: a.sayfalama,
        },
        eslesme: a.eslesme,
      },
    });
    useAlert(t('KATALOG_API.KAYDEDILDI_SENKRON'));
    formAcik.value = false;
    ayar.value = null;
    kaynak.value = '';
    parola.value = '';
    await yukle();
    if (data?.baglanti?.id) senkronuIzle(data.baglanti.id);
  } catch (e) {
    hata.value = e?.response?.data?.error || t('KATALOG_API.HATA');
  } finally {
    calisiyor.value = false;
  }
};

const cevir = async b => {
  try {
    await axios.patch(`${taban.value}/${b.id}`, {
      baglanti: { senkron_acik: !b.senkron_acik },
    });
    await yukle();
    if (!b.senkron_acik) senkronuIzle(b.id);
  } catch (e) {
    hata.value = e?.response?.data?.error || t('KATALOG_API.HATA');
  }
};

const simdi = async b => {
  try {
    await axios.post(`${taban.value}/${b.id}/simdi`);
    useAlert(t('KATALOG_API.KUYRUKTA'));
    senkronuIzle(b.id);
  } catch (e) {
    hata.value = e?.response?.data?.error || t('KATALOG_API.HATA');
  }
};

// Silme: tarayici uyarisi yerine kendi diyalogumuz. Baglanti silinince
// urunlerin ne olacagi musterinin karari - varsayilan "kalsin"; "sil"
// yalnizca bu baglantidan gelen urunleri kaldirir.
const silDialog = ref(null);
const silinecek = ref(null);
const urunSecimi = ref('kalsin');
const siliniyor = ref(false);

const silAdet = computed(() => Number(silinecek.value?.urun_sayisi) || 0);

const silSecenekleri = computed(() => [
  {
    deger: 'kalsin',
    baslik: t('KATALOG_API.SIL_KALSIN'),
    not: t('KATALOG_API.SIL_KALSIN_NOT', { adet: silAdet.value }),
  },
  {
    deger: 'sil',
    baslik: t('KATALOG_API.SIL_URUNLER'),
    not: t('KATALOG_API.SIL_URUNLER_NOT', { adet: silAdet.value }),
  },
]);

const silEtiketi = computed(() =>
  urunSecimi.value === 'sil' && silAdet.value
    ? t('KATALOG_API.SIL_ONAYLA_URUNLER', { adet: silAdet.value })
    : t('KATALOG_API.SIL_ONAYLA')
);

const silIste = b => {
  silinecek.value = b;
  urunSecimi.value = 'kalsin';
  silDialog.value?.open();
};

const silOnayla = async () => {
  const b = silinecek.value;
  if (!b || siliniyor.value) return;

  siliniyor.value = true;
  try {
    const secim = silAdet.value ? urunSecimi.value : 'kalsin';
    const { data } = await axios.delete(`${taban.value}/${b.id}`, {
      params: { urunler: secim },
    });
    const silinen = Number(data?.silinen_urun) || 0;
    useAlert(
      silinen
        ? t('KATALOG_API.SILINDI_URUNLER', { adet: silinen })
        : t('KATALOG_API.SILINDI_KALDI')
    );
    silDialog.value?.close();
    await yukle();
  } catch (e) {
    hata.value = e?.response?.data?.error || t('KATALOG_API.HATA');
    silDialog.value?.close();
  } finally {
    siliniyor.value = false;
  }
};

const dosyaSecildi = olay => {
  dosya.value = (olay.target.files && olay.target.files[0]) || null;
};

const zaman = d => (d ? new Date(d).toLocaleString('tr-TR') : '—');
const eslesmeSatiri = e => Object.entries(e || {}).map(([k, v]) => `${k} ← ${v}`).join(', ');
</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('KATALOG_API.BASLIK') }}
        </h2>
        <p class="max-w-2xl text-sm text-n-slate-11">
          {{ t('KATALOG_API.ACIKLAMA') }}
        </p>
      </div>
      <Button
        size="sm"
        variant="solid"
        icon="i-lucide-plus"
        :label="t('KATALOG_API.YENI')"
        @click="formAcik = !formAcik"
      />
    </div>

    <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

    <!-- Sihirbaz -->
    <div
      v-if="formAcik"
      class="flex flex-col gap-3 p-4 rounded-xl border border-solid border-n-weak bg-n-alpha-1"
    >
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('KATALOG_API.DOKUMAN') }}
        </span>
        <textarea
          v-model="kaynak"
          rows="2"
          class="!mb-0 text-sm"
          :placeholder="t('KATALOG_API.DOKUMAN_IPUCU')"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('KATALOG_API.DOSYA') }}
        </span>
        <input
          type="file"
          class="!mb-0 text-sm"
          accept=".pdf,.json,.yaml,.yml,.txt,.md,.csv"
          @change="dosyaSecildi"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('KATALOG_API.TEMEL_ADRES') }}
        </span>
        <input
          v-model="temelAdres"
          type="text"
          class="!mb-0 text-sm"
          placeholder="https://magazam.butiksistem.com"
        />
      </label>

      <div class="flex flex-wrap gap-2">
        <label class="flex flex-col flex-1 gap-1 min-w-48">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('KATALOG_API.KULLANICI') }}
          </span>
          <input v-model="kullanici" type="text" class="!mb-0 text-sm" />
        </label>
        <label class="flex flex-col flex-1 gap-1 min-w-48">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('KATALOG_API.PAROLA') }}
          </span>
          <input v-model="parola" type="password" class="!mb-0 text-sm" />
        </label>
      </div>
      <p class="text-xs text-n-slate-10">{{ t('KATALOG_API.KIMLIK_NOT') }}</p>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="solid"
          :label="t('KATALOG_API.INCELE')"
          :is-loading="calisiyor"
          :disabled="calisiyor || (!kaynak.trim() && !dosya)"
          @click="incele"
        />
        <span v-if="calisiyor" class="flex items-center gap-2 text-xs text-n-slate-11">
          <Spinner class="text-n-brand size-4" />
          {{ t('KATALOG_API.INCELENIYOR') }}
        </span>
      </div>

      <!-- Uc secici. Model urun ucunu yanlis secebiliyor (marka listesi
           ile urun listesi birbirine cok benziyor); duzeltmenin yolu
           acik olmali. -->
      <div v-if="uclar.length > 1" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('KATALOG_API.UC_SEC') }}
        </span>
        <div class="flex flex-wrap items-center gap-2">
          <select v-model="seciliUc" class="!mb-0 flex-1 min-w-64 text-sm">
            <option v-for="uc in uclar" :key="uc.index" :value="uc.index">
              {{ uc.yontem }} — {{ uc.ad }}
            </option>
          </select>
          <Button
            size="sm"
            variant="ghost"
            :label="t('KATALOG_API.YENIDEN')"
            :is-loading="calisiyor"
            :disabled="calisiyor || seciliUc === null"
            @click="cozumle"
          />
        </div>
        <span class="text-xs text-n-slate-10">
          {{ t('KATALOG_API.UC_SEC_NOT') }}
        </span>
      </div>

      <!-- Model emin olamadigi yerde soruyor. Uydurmaktansa sormak. -->
      <template v-if="ayar && ayar.sorular && ayar.sorular.length">
        <div class="flex flex-col gap-3 p-3 rounded-lg bg-n-amber-9/10">
          <span class="text-xs font-medium text-n-amber-11">
            {{ t('KATALOG_API.SORULAR') }}
          </span>
          <label
            v-for="s in ayar.sorular"
            :key="s.anahtar"
            class="flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-12">{{ s.soru }}</span>
            <span v-if="s.ipucu" class="text-xs text-n-slate-10">{{ s.ipucu }}</span>
            <select
              v-if="s.secenekler && s.secenekler.length"
              v-model="cevaplar[s.anahtar]"
              class="!mb-0 text-sm"
            >
              <option value="">—</option>
              <option v-for="o in s.secenekler" :key="o" :value="o">{{ o }}</option>
            </select>
            <input v-else v-model="cevaplar[s.anahtar]" type="text" class="!mb-0 text-sm" />
          </label>
          <div>
            <Button
              size="sm"
              variant="solid"
              :label="t('KATALOG_API.CEVAPLA')"
              :is-loading="calisiyor"
              :disabled="calisiyor"
              @click="cozumle"
            />
          </div>
        </div>
      </template>

      <!-- Cozumlenen ayar -->
      <div v-if="ayar && ayar.yol" class="flex flex-col gap-2">
        <div
          class="px-3 py-2 text-xs rounded-lg"
          :class="ayar.hazir ? 'bg-n-teal-9/10 text-n-teal-11' : 'bg-n-alpha-2 text-n-slate-11'"
        >
          {{ ayar.hazir ? t('KATALOG_API.HAZIR') : t('KATALOG_API.SORULU_KAYDET') }}
        </div>
        <dl class="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2 text-n-slate-11">
          <div><dt class="inline font-medium">{{ t('KATALOG_API.AD') }}:</dt> {{ ayar.ad }}</div>
          <div><dt class="inline font-medium">{{ t('KATALOG_API.UC') }}:</dt> {{ ayar.yontem }} {{ ayar.yol }}</div>
          <div><dt class="inline font-medium">{{ t('KATALOG_API.DIZI') }}:</dt> {{ ayar.dizi_yolu || '(kök)' }}</div>
          <div class="sm:col-span-2">
            <dt class="inline font-medium">{{ t('KATALOG_API.ESLESME') }}:</dt>
            {{ eslesmeSatiri(ayar.eslesme) }}
          </div>
        </dl>
        <ul
          v-if="ayar.notlar && ayar.notlar.length"
          class="flex flex-col gap-1 pl-4 m-0 text-xs list-disc text-n-slate-11"
        >
          <li v-for="(n, i) in ayar.notlar" :key="i">{{ n }}</li>
        </ul>
        <div>
          <Button
            size="sm"
            variant="solid"
            :label="t('KATALOG_API.KAYDET')"
            :is-loading="calisiyor"
            :disabled="calisiyor"
            @click="kaydet"
          />
        </div>
      </div>
    </div>

    <!-- Mevcut baglantilar -->
    <div v-if="yukleniyor" class="flex justify-center p-6">
      <Spinner class="text-n-brand" />
    </div>

    <p
      v-else-if="!baglantilar.length"
      class="px-4 py-6 text-sm text-center rounded-xl bg-n-alpha-1 text-n-slate-11"
    >
      {{ t('KATALOG_API.BOS') }}
    </p>

    <ul v-else class="flex flex-col gap-3 m-0 list-none">
      <li
        v-for="b in baglantilar"
        :key="b.id"
        class="flex flex-col gap-2 p-4 rounded-xl border border-solid border-n-weak"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="flex items-center gap-2">
            <span class="text-sm font-medium text-n-slate-12">{{ b.ad }}</span>
            <span
              class="px-1.5 py-0.5 text-[10px] rounded"
              :class="b.senkron_acik ? 'bg-n-teal-9/10 text-n-teal-11' : 'bg-n-alpha-2 text-n-slate-11'"
            >
              {{ b.senkron_acik ? t('KATALOG_API.ACIK') : t('KATALOG_API.KAPALI') }}
            </span>
            <span
              v-if="bekleyen === b.id"
              class="flex items-center gap-1 text-[11px] text-n-slate-11"
            >
              <Spinner class="text-n-brand size-3" />
              {{ t('KATALOG_API.SURUYOR') }}
            </span>
          </span>
          <div class="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="ghost"
              :label="b.senkron_acik ? t('KATALOG_API.DURDUR') : t('KATALOG_API.BASLAT')"
              @click="cevir(b)"
            />
            <Button size="sm" variant="ghost" :label="t('KATALOG_API.SIMDI')" @click="simdi(b)" />
            <Button size="sm" variant="ghost" color="ruby" :label="t('KATALOG_API.SIL')" @click="silIste(b)" />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-1 text-xs sm:grid-cols-3 text-n-slate-11">
          <span>{{ t('KATALOG_API.URUN') }}: {{ b.urun_sayisi }}</span>
          <span>{{ t('KATALOG_API.ARALIK') }}: {{ b.aralik_dk }} dk</span>
          <span>{{ t('KATALOG_API.SON') }}: {{ zaman(b.son_senkron_at) }}</span>
        </div>

        <p v-if="b.son_hata" class="text-xs text-n-ruby-11">{{ b.son_hata }}</p>
        <p v-else-if="b.son_ozet && b.son_ozet.okunan" class="text-xs text-n-slate-10">
          {{
            t('KATALOG_API.OZET', {
              okunan: b.son_ozet.okunan,
              eklenen: b.son_ozet.eklenen,
              guncellenen: b.son_ozet.guncellenen,
            })
          }}
        </p>
      </li>
    </ul>

    <Dialog
      ref="silDialog"
      type="alert"
      :title="silinecek ? t('KATALOG_API.SIL_BASLIK', { ad: silinecek.ad }) : ''"
      :confirm-button-label="silEtiketi"
      :cancel-button-label="t('KATALOG_API.VAZGEC')"
      :is-loading="siliniyor"
      @confirm="silOnayla"
      @close="silinecek = null"
    >
      <template #description>
        <p class="mb-0 text-sm text-n-slate-11">
          {{
            silAdet
              ? t('KATALOG_API.SIL_ACIKLAMA', { adet: silAdet })
              : t('KATALOG_API.SIL_ACIKLAMA_BOS')
          }}
        </p>
      </template>
      <div v-if="silAdet" class="flex flex-col gap-2">
        <label
          v-for="s in silSecenekleri"
          :key="s.deger"
          class="flex gap-3 p-3 rounded-lg border border-solid cursor-pointer"
          :class="urunSecimi === s.deger ? 'border-n-brand bg-n-alpha-2' : 'border-n-weak'"
        >
          <input
            v-model="urunSecimi"
            type="radio"
            name="urunSecimi"
            :value="s.deger"
            class="mt-0.5 shrink-0"
          />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">{{ s.baslik }}</span>
            <span class="text-xs text-n-slate-11">{{ s.not }}</span>
          </span>
        </label>
      </div>
    </Dialog>
  </div>
</template>
